import { useEffect, useState } from 'react'
import Link from 'next/link'
import Script from 'next/script'

import Container from '@/components/base/container'
import Typography from '@/components/base/typography'
import { BuilderProps } from '@/components/layout/builder'
import { useLenis } from '@/contexts/lenis'
import { useStore } from '@/contexts/store'
import { useLayoutStore } from '@/store/layout'
import { useLoaderStore } from '@/store/loader'

import { cn } from '@/lib/utils'

type BookProps = BuilderProps & {
  pageProps: {
    acf: {
      title: string
      link_alternative_book: {
        title: string
        url: string
      }
      text: string
      address: string
      contacts: string
      apertura: string
      iframe_url: string
      layout: string
      rules_paragraph?: string
      terms_conditions?: string
      needs_check: boolean
    }
  }
}

const Book = ({ pageProps }: BookProps) => {
  const { strings } = useStore()
  const setLoaderState = useLoaderStore((state) => state.setLoaderState)
  const setLayoutState = useLayoutStore((state) => state.setLayoutState)
  const { footer } = strings || {}
  const [termsChecked, setTermsChecked] = useState(
    !pageProps?.acf?.needs_check || false
  )
  const { lenis } = useLenis()

  useEffect(() => {
    setLoaderState({ heroCanPlay: true })
    setLoaderState({ isPosterReady: true })
  }, [setLoaderState])

  useEffect(() => {
    if (termsChecked) {
      if (
        typeof window !== 'undefined' &&
        typeof (window as any).iFrameResize === 'function'
      ) {
        ;(window as any).iFrameResize({
          resizedCallback: function () {
            if (window.parent) {
              const iframeId = window.frameElement?.id
              if (iframeId)
                window.parent.postMessage(
                  JSON.stringify({ type: 'scrollToIframe', iframeId }),
                  '*'
                )
            }
          }
        })
      }
    }
  }, [termsChecked])

  useEffect(() => {
    if (!lenis) return
    window.addEventListener('message', (event) => {
      try {
        const dataString =
          typeof event.data === 'string'
            ? event.data
            : JSON.stringify(event.data)
        if (
          dataString.includes('rebis-restaurant') &&
          dataString.includes('mutationObserver')
        ) {
          const iframe = document.getElementById('rebis-restaurant')
          if (iframe && lenis) lenis.scrollTo(iframe)
        }
      } catch (error) {
        console.error('Error processing message event:', error)
      }
    })
  }, [lenis])

  useEffect(() => {
    setLayoutState({ viewLogo: true, footerVisible: false })

    return () => {
      setLayoutState({ viewLogo: false, footerVisible: false })
    }
  }, [setLayoutState])

  return (
    <div className="flex h-auto w-full flex-col">
      <Container className="bg-black pb-[40px] pt-[90px] md:pb-[90px] md:pt-[180px]">
        <div className={cn('grid w-full grid-cols-12 gap-gutter', 'md:gap-0')}>
          <div className="col-span-12 flex flex-col gap-10 lg:col-span-7">
            <div className="flex gap-[40px]">
              {pageProps?.acf?.title ? (
                <Typography
                  component="h1"
                  className="text-[3.6rem] font-light leading-[1] md:text-[7.2rem]"
                >
                  {`<span>${pageProps.acf.title}</span>`}
                </Typography>
              ) : null}
              {pageProps?.acf?.link_alternative_book?.title ? (
                <Link
                  scroll={false}
                  href={pageProps.acf.link_alternative_book.url}
                  className="link opacity-30 transition-opacity hover:opacity-100"
                >
                  <Typography
                    component="h1"
                    className="text-[3.6rem] font-light leading-[1] md:text-[7.2rem]"
                  >
                    {`<span>${pageProps.acf.link_alternative_book.title}</span>`}
                  </Typography>
                </Link>
              ) : null}
            </div>
            {pageProps?.acf?.text ? (
              <Typography className="hidden text-[1.8rem] leading-[1.16] md:block md:text-[3.6rem]">
                {pageProps.acf.text}
              </Typography>
            ) : null}
          </div>
          <div className="col-span-12 mt-[25px] flex flex-col gap-8 md:mt-[50px] lg:col-span-4 lg:col-start-9 lg:mt-0">
            {pageProps.acf.apertura ? (
              <Typography className="text-[1.8rem] leading-[1.16] md:text-[3.6rem]">
                {pageProps.acf.apertura}
              </Typography>
            ) : null}

            {pageProps.acf.address ? (
              <Typography className="text-[1.3rem] leading-[1.3] md:text-[2rem]">
                {pageProps.acf.address}
              </Typography>
            ) : null}
            {pageProps.acf.contacts ? (
              <Typography className="text-[1.3rem] uppercase leading-[1.3]">
                {pageProps.acf.contacts}
              </Typography>
            ) : null}
          </div>
        </div>
      </Container>
      <Container className="bg-white py-[40px] md:py-[90px]">
        <div className={cn('grid w-full grid-cols-12 gap-gutter', 'md:gap-0')}>
          <div className="col-span-12 flex-col gap-8 text-dark md:col-span-8 md:col-start-3">
            {pageProps?.acf?.rules_paragraph ? (
              <Typography className="leading-[1.6] md:text-[1.8rem]">
                {pageProps.acf.rules_paragraph}
              </Typography>
            ) : null}
          </div>
          {pageProps?.acf?.needs_check ? (
            <div className="col-span-12 mt-[4rem] md:col-span-8 md:col-start-3">
              <div className="mx-auto grid gap-y-[1.6rem]">
                <label className="flex items-start space-x-[1rem] text-[#5F6368]">
                  <input
                    type="checkbox"
                    checked={termsChecked}
                    className="mt-[0.5rem] h-[1.3rem] w-[1.3rem]"
                    onChange={(event) => setTermsChecked(event.target.checked)}
                  />
                  <Typography>{pageProps?.acf?.terms_conditions}</Typography>
                </label>
              </div>
            </div>
          ) : null}
          {termsChecked ? (
            <div
              className="col-span-12 flex flex-col gap-10 md:col-span-8 md:col-start-3"
              id="rebis-iframe"
            >
              <Script
                src="https://www.covermanager.com/js/iframeResizer/iframeResizer.min.js"
                strategy="afterInteractive"
                onLoad={() => {
                  if (
                    typeof window !== 'undefined' &&
                    typeof (window as any).iFrameResize === 'function'
                  ) {
                    ;(window as any).iFrameResize()
                  }
                }}
              />
              <iframe
                id="rebis-restaurant"
                title="Rebis Booking"
                src={pageProps?.acf?.iframe_url}
                allow="payment"
                width="100%"
                style={{ width: '1px', minWidth: '100%', border: 'none' }}
              />
            </div>
          ) : null}
        </div>
      </Container>
      <div className="flex w-full bg-white px-[4rem] py-[3.6rem]">
        <Typography
          className={cn(
            'flex text-[1.28rem] capitalize tracking-[0.0384rem]',
            'text-dark/30'
          )}
          data-motion-colophon
        >
          {footer?.colophon.replace('[YEAR]', new Date().getFullYear())}
        </Typography>
      </div>
    </div>
  )
}

export default Book
